import React from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import { alpha } from '@mui/material/styles'

const StatCard = ({
  icon: Icon,
  label,
  value,
  afterValue,
  caption,
  color = 'anBlue',
  trend,
  highlight = false,
  onClick,
}) => {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={(theme) => {
        const paletteColor = theme.palette[color]?.main || color
        const lightBg = alpha(paletteColor, highlight ? 0.1 : 0.07)

        return {
          p: 2,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: highlight ? alpha(paletteColor, 0.035) : 'background.paper',
          cursor: onClick ? 'pointer' : 'default',
          borderColor: alpha(paletteColor, 0.22),
          boxShadow: '0 8px 24px rgba(15,23,42,0.055)',
          transition:
            'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',

          '&:before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 5,
            bgcolor: paletteColor,
          },

          '&:hover': onClick
            ? {
                transform: 'translateY(-2px)',
                boxShadow: '0 16px 36px rgba(15,23,42,0.12)',
                borderColor: alpha(paletteColor, 0.45),
              }
            : {},
        }
      }}
    >
      <Stack direction="row" spacing={1.75} alignItems="flex-start">
        {Icon && (
          <Box
            sx={(theme) => {
              const paletteColor = theme.palette[color]?.main || color

              return {
                width: 48,
                height: 48,
                borderRadius: 2.5,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(paletteColor, 0.09),
                color: paletteColor,
                border: `1px solid ${alpha(paletteColor, 0.22)}`,
                flex: '0 0 auto',
              }
            }}
          >
            <Icon sx={{ fontSize: 32 }} />
          </Box>
        )}

        <Box sx={{ minWidth: 0, flex: 1, gap: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={1}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 800,
                letterSpacing: 0.45,
                textTransform: 'uppercase',
              }}
            >
              {label}
            </Typography>

            {trend && (
              <Chip
                label={trend}
                size="small"
                sx={(theme) => {
                  const paletteColor = theme.palette[color]?.main || color
                  return {
                    height: 22,
                    borderRadius: 1,
                    fontWeight: 800,
                    bgcolor: alpha(paletteColor, 0.08),
                    color: paletteColor,
                  }
                }}
              />
            )}
          </Stack>

          <Stack direction="row" alignItems="baseline" gap={1}>
            <Typography
              // variant="h3"
              noWrap
              sx={(theme) => {
                const paletteColor = theme.palette[color]?.main || color
                return {
                  mt: 0.2,
                  fontWeight: 950,
                  color: paletteColor,
                }
              }}
            >
              {value ?? '—'}
            </Typography>
            {afterValue && (
              <Typography
                variant="h5"
                noWrap
                sx={(theme) => {
                  const paletteColor = theme.palette[color]?.main || color
                  return {
                    fontWeight: 800,
                    lineHeight: 1,
                    color: paletteColor,
                  }
                }}
              >
                {afterValue}
              </Typography>
            )}
          </Stack>
        </Box>
      </Stack>
      {caption && (
        <Typography
          variant="body2"
          sx={{
            mt: 1,
            color: 'text.secondary',
            fontWeight: 600,
          }}
          noWrap
          title={caption}
        >
          {caption}
        </Typography>
      )}
    </Paper>
  )
}

export default StatCard
